import { useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { createSelector } from 'reselect';

import apiCaller from 'services/Api/ApiCaller';
import { API_URL } from 'settings';
import {
  BUY,
  LEGACY_EXISTING_PATH,
  PER_ASSET_NOTIONAL,
  SELL,
  selectors,
} from 'state';
import type { StoreState } from '../../state/storeState';

export type NotionalCapacityResult = {
  financial_instrument: number;
  transaction: number;
  applies: boolean;
  allowed: boolean;
  exposure_change: 'INCREASE' | 'REDUCTION' | 'FLAT';
  valuation_basis: string | null;
  configured_limit_reporting: string | null;
  current_value_reporting: string | null;
  proposed_value_reporting: string | null;
  remaining_value_reporting: string | null;
  notional_max_additional_quantity: number | null;
  notional_max_add_contracts: number | null;
  max_order_quantity: number | null;
  max_order_contracts: number | null;
  contract_multiplier: number | null;
  valuation_price_native: string | null;
  reporting_unit_value: string | null;
  valuation_currency_id: number | null;
  reporting_currency_id: number | null;
  quote_timestamp: string | null;
  reason_code: string;
  binding_constraints: string[];
};

type RequestSpec = {
  generation: number;
  instrumentId: number;
  transaction: number;
};

export type NotionalCapacityState = {
  /** True while a V2 or not-yet-classified V2-session ticket is backend-owned. */
  usesBackend: boolean;
  loading: boolean;
  error: string | null;
  result: NotionalCapacityResult | null;
};

const DEBOUNCE_MS = 250;
const LEGACY_CONTEXT = {
  usesBackend: false,
  refreshFingerprint: 'legacy',
};

const capacityContextSelector = (instrumentId: number) =>
  createSelector(
    [
      selectors.activeSession,
      selectors.financialInstruments,
      selectors.underlyingAssets,
      selectors.player,
    ],
    (session, instruments, assets, player) => {
      if (session.instrument_limit_mode !== PER_ASSET_NOTIONAL) {
        // Returning the same object keeps V1 tickets from re-rendering when an
        // unrelated asset quote changes. V1 still uses its existing local
        // arithmetic and starts no capacity request.
        return LEGACY_CONTEXT;
      }

      const instrument = instruments[instrumentId];
      if (!instrument) return LEGACY_CONTEXT;
      const asset = assets[instrument.underlying_asset];
      const linkedAsset = asset?.corresponding_spot_asset
        ? assets[asset.corresponding_spot_asset]
        : undefined;
      const valuationAsset = linkedAsset ?? asset;
      const valuationCurrency =
        asset?.resourcetype === 'FX' || asset?.resourcetype === 'FXCross'
          ? asset.base_currency
          : valuationAsset?.variable_currency;
      const reportingFxAsset = Object.values(assets).find(candidate => {
        if (
          valuationCurrency == null ||
          valuationCurrency === session.reporting_currency ||
          (candidate.resourcetype !== 'FX' &&
            candidate.resourcetype !== 'FXCross')
        ) {
          return false;
        }
        return (
          (candidate.base_currency === valuationCurrency &&
            candidate.variable_currency === session.reporting_currency) ||
          (candidate.variable_currency === valuationCurrency &&
            candidate.base_currency === session.reporting_currency)
        );
      });
      const eligibility = session.notional_limit_eligibility?.find(
        row => row.underlying_asset === asset?.id,
      );

      return {
        // A missing V2 eligibility row fails toward backend ownership. The
        // endpoint authoritatively returns applies=false for excluded products
        // and fails closed for malformed eligible configuration.
        usesBackend: eligibility?.runtime_path !== LEGACY_EXISTING_PATH,
        refreshFingerprint: JSON.stringify([
          session.scenario_date,
          session.elapsed_days,
          session.spread_widener,
          session.bond_leverage,
          session.equity_leverage,
          session.etf_equities_leverage,
          session.etf_commodities_leverage,
          session.allow_short_sell,
          session.allow_short_only_on_fx,
          session.allow_simplified_option_writing,
          instrument.amount,
          instrument.collateral,
          instrument.simplified_option,
          asset?.bid,
          asset?.ask,
          linkedAsset?.bid,
          linkedAsset?.ask,
          reportingFxAsset?.bid,
          reportingFxAsset?.ask,
          player.remaining_short_sell_amount_limit,
          player.remaining_fx_amount_limit,
          player.remaining_reporting_absolute_bond_leverage,
          player.remaining_reporting_absolute_equity_leverage,
          player.remaining_reporting_absolute_etf_equities_leverage,
          player.remaining_reporting_absolute_etf_commodities_leverage,
          Object.values(player.wallets ?? {}).map(wallet => [
            wallet.currency,
            wallet.amount,
          ]),
        ]),
      };
    },
  );

/**
 * One active-ticket-only capacity client. It serializes requests, trails rapid
 * quote/position changes by 250 ms, and ignores responses from stale intents.
 */
export const useNotionalCapacity = (
  instrumentId: number,
  transaction: number,
  enabled = true,
): NotionalCapacityState => {
  const selectCapacityContext = useMemo(
    () => capacityContextSelector(instrumentId),
    [instrumentId],
  );
  const capacityContext = useSelector<
    StoreState,
    ReturnType<typeof selectCapacityContext>
  >(selectCapacityContext, shallowEqual);
  const usesBackend = Boolean(enabled && capacityContext.usesBackend);
  const { refreshFingerprint } = capacityContext;

  const [result, setResult] = useState<NotionalCapacityResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const latestGenerationRef = useRef(0);
  const inFlightRef = useRef(false);
  const queuedRef = useRef<RequestSpec | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startRequestRef = useRef<(spec: RequestSpec) => void>(() => undefined);

  const clearTimer = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
  };

  startRequestRef.current = (spec: RequestSpec) => {
    if (inFlightRef.current || !mountedRef.current) {
      queuedRef.current = spec;
      return;
    }
    inFlightRef.current = true;
    apiCaller<{ financial_instrument: number; transaction: number }>({
      path: '/orders/notional-capacity',
      method: 'POST',
      data: {
        financial_instrument: spec.instrumentId,
        transaction: spec.transaction,
      },
      apiUrl: API_URL,
    })
      .then(({ result: response, status }) => {
        const isLatest =
          mountedRef.current && latestGenerationRef.current === spec.generation;
        if (!isLatest) return;
        if (
          status !== 200 ||
          !response ||
          response.financial_instrument !== spec.instrumentId ||
          response.transaction !== spec.transaction
        ) {
          throw new Error('Capacity preview is unavailable.');
        }
        setResult(response as NotionalCapacityResult);
        setError(null);
        setLoading(false);
      })
      .catch(requestError => {
        if (
          mountedRef.current &&
          latestGenerationRef.current === spec.generation
        ) {
          setResult(null);
          setError(
            requestError instanceof Error
              ? requestError.message
              : 'Capacity preview is unavailable.',
          );
          setLoading(false);
        }
      })
      .finally(() => {
        inFlightRef.current = false;
        const queued = queuedRef.current;
        if (!mountedRef.current || !queued) return;
        queuedRef.current = null;
        clearTimer();
        timerRef.current = setTimeout(
          () => startRequestRef.current(queued),
          DEBOUNCE_MS,
        );
      });
  };

  useEffect(() => {
    const generation = latestGenerationRef.current + 1;
    latestGenerationRef.current = generation;
    clearTimer();
    queuedRef.current = null;

    if (!usesBackend) {
      setResult(null);
      setError(null);
      setLoading(false);
      return undefined;
    }

    const spec = { generation, instrumentId, transaction };
    queuedRef.current = spec;
    setLoading(true);
    setError(null);
    timerRef.current = setTimeout(() => {
      const latest = queuedRef.current;
      if (!latest || latest.generation !== generation) return;
      queuedRef.current = null;
      startRequestRef.current(latest);
    }, DEBOUNCE_MS);

    return clearTimer;
  }, [instrumentId, refreshFingerprint, transaction, usesBackend]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      latestGenerationRef.current += 1;
      queuedRef.current = null;
      clearTimer();
    };
  }, []);

  return { usesBackend, loading, error, result };
};

export const isExposureReduction = (
  transaction: number,
  signedPosition: number,
) =>
  (transaction === BUY && signedPosition < 0) ||
  (transaction === SELL && signedPosition > 0);

/** Select backend maxima only for V2; V1/excluded local arithmetic stays intact. */
export const resolveNotionalMaximum = ({
  localMaximum,
  capacity,
  transaction,
  signedPosition,
  contracts = false,
}: {
  localMaximum: number;
  capacity: NotionalCapacityState;
  transaction: number;
  signedPosition: number;
  contracts?: boolean;
}) => {
  if (!capacity.usesBackend || capacity.result?.applies === false) {
    return localMaximum;
  }
  if (capacity.loading || capacity.error) {
    return isExposureReduction(transaction, signedPosition) ? localMaximum : 0;
  }
  if (capacity.result?.applies) {
    return Math.max(
      0,
      Number(
        contracts
          ? (capacity.result.max_order_contracts ?? 0)
          : (capacity.result.max_order_quantity ?? 0),
      ),
    );
  }
  // A preview error or in-flight request cannot authorize a new exposure. A
  // close/reduction remains usable through the already-established V1 maximum.
  return isExposureReduction(transaction, signedPosition) ? localMaximum : 0;
};

export const notionalCapacityStatus = (capacity: NotionalCapacityState) => {
  if (!capacity.usesBackend) return null;
  if (capacity.loading) return 'Checking backend capacity…';
  if (capacity.error)
    return 'Backend capacity unavailable; increases disabled.';
  if (capacity.result?.quote_timestamp) {
    return `Backend capacity quote: ${new Date(
      capacity.result.quote_timestamp,
    ).toLocaleString()}`;
  }
  return 'Backend notional capacity applied.';
};
